/**
 * Bearer tokens for backend requests.
 *
 * The backend validates the token audience against AZURE_CLIENT_ID, which is
 * exactly the `aud` of our id token — so the id token is what we send and no
 * separate access token has to be acquired for a dedicated API scope.
 */
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { msalInstance, LOGIN_REQUEST } from './msalConfig';
import { getIdTokenClaims } from './tokenClaims';

/** Renew this far ahead of `exp` so an in-flight request can't race the expiry. */
const EXPIRY_SKEW_SECONDS = 120;

/**
 * msal-browser decides cache freshness by the *access* token, so the id token it
 * hands back alongside can already be expired. We therefore judge the id token's
 * own `exp` and force the refresh ourselves. An unreadable `exp` counts as
 * expiring: a needless refresh is cheaper than a 401.
 */
const isIdTokenExpiringSoon = (account: AccountInfo): boolean => {
  const { exp } = getIdTokenClaims(account);
  if (typeof exp !== 'number') return true;
  return exp - Date.now() / 1000 <= EXPIRY_SKEW_SECONDS;
};

/**
 * The id token for the signed-in user, silently renewed when it is close to
 * expiry. Returns null when nobody is signed in or the silent flow fails, in
 * which case the caller sends an unauthenticated request and lets the backend
 * decide — that keeps a locally running backend without AZURE_* configured
 * usable.
 */
export async function acquireIdToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount();
  if (!account) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...LOGIN_REQUEST,
      account,
      forceRefresh: isIdTokenExpiringSoon(account),
    });
    return result.idToken || null;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await requestInteractiveSignIn();
      return null;
    }
    console.error('[BRR][MSAL] Silent token acquisition failed:', error);
    return null;
  }
}

/**
 * Never rethrows: the caller's contract is a token or null, so a failed redirect
 * must not surface at the call site as an unrelated-looking API error. A page
 * fires many requests at once, so when several of them need interaction MSAL
 * rejects all but the first with `interaction_in_progress` — that rejection is
 * expected here and only logged.
 */
async function requestInteractiveSignIn(): Promise<void> {
  try {
    await msalInstance.acquireTokenRedirect(LOGIN_REQUEST);
  } catch (error) {
    console.error('[BRR][MSAL] Interactive sign-in could not be started:', error);
  }
}

/**
 * Best-effort token without awaiting, for the `beforeunload` handler: the
 * browser tears the page down before a promise can settle, so the cached token
 * is the only one obtainable there.
 */
export function getCachedIdToken(): string | null {
  return msalInstance.getActiveAccount()?.idToken || null;
}
